import React, { useState } from 'react'

import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Modal, Button, Input, Alert } from 'antd'
import { useTranslation } from 'react-i18next'

interface ConfirmModalProps {
  open: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText?: string;
  danger?: boolean;
  alertType?: 'success' | 'info' | 'warning' | 'error';
}

/**
 * 通用確認 Modal 組件
 * 用於需要使用者輸入特定文字才能執行的危險操作
 *
 * @param open - 是否顯示 Modal
 * @param onClose - 關閉 Modal 的回調函數
 * @param onConfirm - 確認操作的回調函數
 * @param title - Modal 標題
 * @param description - 警告描述文字
 * @param confirmText - 需要輸入的確認文字（預設為 'confirm'）
 * @param danger - 是否為危險操作（紅色按鈕，預設為 true）
 * @param alertType - Alert 類型（預設為 'warning'）
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'confirm',
  danger = true,
  alertType = 'warning',
}) => {
  const { t } = useTranslation()
  const [inputText, setInputText] = useState('')
  const isConfirmValid = inputText.toLowerCase() === confirmText.toLowerCase()

  const handleClose = () => {
    setInputText('')
    onClose && onClose()
  }

  const handleConfirm = () => {
    if (isConfirmValid) {
      onConfirm && onConfirm()
      handleClose()
    }
  }

  return (
    <Modal
      centered
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
      destroyOnHidden
    >
      <div style={{ padding: '8px 0' }}>
        <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExclamationCircleOutlined
            style={{
              fontSize: '24px',
              color: danger ? '#ff4d4f' : '#faad14'
            }}
          />
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            {title}
          </h3>
        </div>

        {description && (
          <Alert
            message={description}
            type={alertType}
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}

        <div style={{ marginBottom: '20px' }}>
          <p style={{ marginBottom: '12px', color: '#666', fontSize: '14px' }}>
            {(() => {
              const translatedText = t('common.confirm_modal.type_to_confirm', { text: '{{TEXT_PLACEHOLDER}}' })
              const parts = translatedText.split('{{TEXT_PLACEHOLDER}}')
              return (
                <>
                  {parts[0]}
                  <code style={{
                    padding: '2px 8px',
                    margin: '0 4px',
                    backgroundColor: '#fff1f0',
                    border: '1px solid #ffccc7',
                    borderRadius: '4px',
                    color: '#cf1322',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                  }}>
                    {confirmText}
                  </code>
                  {parts[1]}
                </>
              )
            })()}
          </p>
          <Input
            placeholder={t('common.confirm_modal.type_to_confirm', { text: confirmText })}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onPressEnter={() => isConfirmValid && handleConfirm()}
            size="large"
            autoFocus
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose}>
            {t('common.button.cancel')}
          </Button>
          <Button
            type="primary"
            danger={danger}
            disabled={!isConfirmValid}
            onClick={handleConfirm}
          >
            {t('common.button.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmModal
